import { Accessibility, Co2, SpaOutlined } from '@mui/icons-material';
import { ReactNode } from 'react';

import { ScoreType } from '@/domain/impact/impact.entity';
import { TopicsGlobalScoreData } from '@/domain/impact-calculate/entities/calculate.entity';
import { language, translate } from '@/ui/i18n';
import { getColorForScore } from '@/ui/utils/get-color-for-score';

import { ShowDetailsMode } from '../ProjectScores';

export function prepareScoreCardData(
  topicsGlobalScoreData: TopicsGlobalScoreData,
  showDetailsMode: ShowDetailsMode,
) {
  const { co2, env, ess } = topicsGlobalScoreData;
  const { projectMode } = topicsGlobalScoreData;
  const data: {
    type: ScoreType;
    scoreIcon: ReactNode;
    scoreTextColor: string;
    scoreBackgroundColor: string;
    scoreTitle: string;
    resultValue: number;
    resultUnit: string;
    resultSecondLine: string;
    infos: string;
    selected: boolean;
    scoreLetter: string;
  }[] = [
    {
      type: 'co2',
      scoreIcon: <Co2 />,
      scoreTextColor: getColorForScore(co2.scoreLetter).text,
      scoreBackgroundColor: getColorForScore(co2.scoreLetter).main,
      scoreTitle: translate('Projects.co2'),
      resultValue:
        projectMode === 'tour'
          ? formatCo2ScoreValue(co2)
          : formatCo2ParticipantValue(co2),
      resultUnit:
        projectMode === 'tour' ? getCo2ScoreUnit() : getCo2ParticipantUnit(),
      resultSecondLine:
        projectMode === 'tour' ? '' : formatCo2ResultAndUnit(co2),
      infos: translate('Projects.infos-score.co2'),
      selected: showDetailsMode === 'co2',
      scoreLetter: co2.scoreLetter,
    },
    {
      type: 'env',
      scoreIcon: <SpaOutlined />,
      scoreTextColor: getColorForScore(env.scoreLetter).text,
      scoreBackgroundColor: getColorForScore(env.scoreLetter).main,
      scoreTitle: translate('Projects.env'),
      resultValue: fromatEnvResultValue(env),
      resultUnit: formatEnvResultUnit(),
      resultSecondLine: formatEnvResultSecondLine(env),
      infos: translate('Projects.infos-score.env'),
      selected: showDetailsMode === 'env',
      scoreLetter: env.scoreLetter,
    },
    {
      type: 'ess',
      scoreIcon: <Accessibility />,
      scoreTextColor: getColorForScore(ess.scoreLetter).text,
      scoreBackgroundColor: getColorForScore(ess.scoreLetter).main,
      scoreTitle: translate('Projects.ess'),
      resultValue: formatEssResultValue(ess),
      resultUnit: formatEssResultUnit(),
      resultSecondLine: formatEssResultSecondLine(ess),
      infos: translate('Projects.infos-score.ess'),
      selected: showDetailsMode === 'ess',
      scoreLetter: ess.scoreLetter,
    },
  ];
  return data;
}

// co2
function formatCo2ScoreValue(co2: TopicsGlobalScoreData['co2']) {
  return co2.score;
}
function getCo2ScoreUnit() {
  return 'teCO2';
}
function formatCo2ParticipantValue(co2: TopicsGlobalScoreData['co2']) {
  return co2.kgeCO2ByParticipant
    ? parseFloat(co2.kgeCO2ByParticipant.toFixed(1))
    : 0;
}
function getCo2ParticipantUnit() {
  return translate('Projects.kgeCO2-by-participant');
}
function formatCo2ResultAndUnit(co2: TopicsGlobalScoreData['co2']) {
  const score = co2.score.toLocaleString(language);
  return `${score} teCO2`;
}

// env
function fromatEnvResultValue(env: TopicsGlobalScoreData['env']) {
  return getScoreBasedOnHundred(env.score, env.maxScore);
}
function formatEnvResultUnit() {
  return '/100';
}
function formatEnvResultSecondLine(env: TopicsGlobalScoreData['env']) {
  const score = env.score.toLocaleString(language);
  const maxScore = env.maxScore.toLocaleString(language);
  return `${score} / ${maxScore}`;
}

// ess
function formatEssResultValue(ess: TopicsGlobalScoreData['ess']) {
  return getScoreBasedOnHundred(ess.score, ess.maxScore);
}
function formatEssResultUnit() {
  return '/100';
}
function formatEssResultSecondLine(ess: TopicsGlobalScoreData['ess']) {
  const score = ess.score.toLocaleString(language);
  const maxScore = ess.maxScore.toLocaleString(language);
  return `${score} / ${maxScore}`;
}

function getScoreBasedOnHundred(score: number, maxScore: number): number {
  const scoreBasedOnHundred = maxScore !== 0 ? (score / maxScore) * 100 : 0;
  const scoreRouned = Math.round(scoreBasedOnHundred);
  return scoreRouned;
}
